import { useCallback, useEffect, useRef, useState } from 'react';
import type { NAMTObject } from '@/api/models';
import type { NAMTMessageType } from '@/types/enums';

/**
 * Reconciles the server's `mtObjList` against a local cache of MT commands this
 * session has sent, so pending status is accurate immediately after sending and
 * stays accurate for a grace period after the device confirms receipt.
 *
 * Ported from the mobile app's `DeviceInfo.cs` (`DeviceInfoMTClone`/`MTMap`,
 * `updatePendingMTEntries`) and `DeviceRequests.cs` (where a sent command is
 * cloned into the map right after the REST ack returns an `mtmsn`). Two problems
 * this solves, both present on mobile:
 *
 * 1. A just-sent command may not appear in `mtObjList` yet on the very next
 *    fetch (server-side lag) — the clone is injected as a synthetic pending
 *    entry until the server list catches up, or up to 10s if it never does.
 * 2. The device can mark an MT `messageReceived: true` (e.g. on a mailbox
 *    check) before the underlying setting value it changed has actually
 *    propagated into the rest of `deviceInfoV2`'s response. Reading
 *    `messageReceived` literally at that point would flash the *old* value
 *    for a moment before the new one arrives — "flip-flopping". Mobile's fix:
 *    once `messageReceived` is first observed true, keep reporting it as
 *    pending (`false`) for a further 10 seconds (timed from local receipt,
 *    not server clock, to avoid clock-skew) before letting it go.
 *
 * The 10-second constant matches `DeviceInfo.cs` exactly in both directions
 * (grace period after confirmation, and give-up timeout if never confirmed).
 */
const GRACE_PERIOD_MS = 10_000;

interface ClonedMTEntry {
  mtObj: NAMTObject;
  sentAt: number;
  receivedLocalTime: number | null;
}

function reconcile(cloneMap: Map<string, ClonedMTEntry>, rawList: NAMTObject[]): NAMTObject[] {
  const now = Date.now();
  const merged = [...rawList];

  for (const [key, clone] of cloneMap) {
    const cloneMtmsn = clone.mtObj.mtmsn;
    const cloneType = Number(clone.mtObj.messageType);
    const idx = merged.findIndex((mt) => Number(mt.messageType) === cloneType && mt.mtmsn === cloneMtmsn);

    if (idx !== -1) {
      const serverEntry = merged[idx];
      if (serverEntry.messageReceived === true) {
        if (clone.receivedLocalTime === null) {
          clone.receivedLocalTime = now;
        }
        if (now - clone.receivedLocalTime > GRACE_PERIOD_MS) {
          cloneMap.delete(key);
        } else {
          // Work-around for flip-flopping status — keep showing pending until
          // the device's reported settings value has had time to catch up too.
          merged[idx] = { ...serverEntry, messageReceived: false };
        }
      }
      // else: server already agrees it's still pending, nothing to override.
    } else if (now - clone.sentAt > GRACE_PERIOD_MS) {
      // Never showed up in the server list within the grace window — give up.
      cloneMap.delete(key);
    } else {
      // Not yet visible server-side — inject as a synthetic pending entry so
      // it shows up immediately rather than waiting for the next poll.
      merged.push({ ...clone.mtObj, messageReceived: false });
    }
  }

  return merged;
}

export function usePendingMTTracking(mtObjList: NAMTObject[]) {
  const cloneMapRef = useRef<Map<string, ClonedMTEntry>>(new Map());
  const [mergedMtObjList, setMergedMtObjList] = useState<NAMTObject[]>(mtObjList);

  useEffect(() => {
    setMergedMtObjList(reconcile(cloneMapRef.current, mtObjList));
  }, [mtObjList]);

  /** Call right after a successful send, with the `mtmsn` the server returned. */
  const registerSentMT = useCallback(
    (messageType: NAMTMessageType, mtmsn: number, dataStrList?: string[]) => {
      const key = `${messageType}:${mtmsn}`;
      cloneMapRef.current.set(key, {
        mtObj: { messageType, mtmsn, dataStrList, messageReceived: false },
        sentAt: Date.now(),
        receivedLocalTime: null,
      });
      setMergedMtObjList(reconcile(cloneMapRef.current, mtObjList));
    },
    [mtObjList],
  );

  return { mergedMtObjList, registerSentMT };
}
